import type { Clip, Project, Track } from './types';

/**
 * Immutable update helpers.
 *
 * Every command returns a *new* Project that shares every untouched sub-object
 * with the previous one. That structural sharing is what makes the undo stack
 * affordable: holding 200 history entries costs the size of what actually
 * changed, not 200 copies of the project.
 *
 * It is also what makes React rendering cheap — an unchanged track is
 * reference-equal, so its lane does not re-render.
 */

export function withTrack(project: Project, trackId: string, update: (track: Track) => Track): Project {
  const existing = project.tracks[trackId];
  if (!existing) return project;
  const next = update(existing);
  if (next === existing) return project;
  return { ...project, tracks: { ...project.tracks, [trackId]: next } };
}

export function withTracks(project: Project, trackIds: readonly string[], update: (track: Track) => Track): Project {
  let changed = false;
  const tracks = { ...project.tracks };
  for (const id of trackIds) {
    const existing = tracks[id];
    if (!existing) continue;
    const next = update(existing);
    if (next !== existing) {
      tracks[id] = next;
      changed = true;
    }
  }
  return changed ? { ...project, tracks } : project;
}

export function withClip(project: Project, clipId: string, update: (clip: Clip) => Clip): Project {
  const existing = project.clips[clipId];
  if (!existing) return project;
  const next = update(existing);
  if (next === existing) return project;
  return { ...project, clips: { ...project.clips, [clipId]: next } };
}

export function withClips(project: Project, clipIds: readonly string[], update: (clip: Clip) => Clip): Project {
  let changed = false;
  const clips = { ...project.clips };
  for (const id of clipIds) {
    const existing = clips[id];
    if (!existing) continue;
    const next = update(existing);
    if (next !== existing) {
      clips[id] = next;
      changed = true;
    }
  }
  return changed ? { ...project, clips } : project;
}

/** Inserts a clip and links it to its track, keeping the track's list ordered. */
export function insertClip(project: Project, clip: Clip): Project {
  const track = project.tracks[clip.trackId];
  if (!track) return project;
  const clips = { ...project.clips, [clip.id]: clip };
  const clipIds = [...track.clipIds.filter((id) => id !== clip.id), clip.id].sort(
    (a, b) => (clips[a]?.startTicks ?? 0) - (clips[b]?.startTicks ?? 0),
  );
  return {
    ...project,
    clips,
    tracks: { ...project.tracks, [track.id]: { ...track, clipIds } },
  };
}

export function insertClips(project: Project, newClips: readonly Clip[]): Project {
  let next = project;
  for (const clip of newClips) next = insertClip(next, clip);
  return next;
}

export function removeClips(project: Project, clipIds: readonly string[]): Project {
  const doomed = new Set(clipIds.filter((id) => project.clips[id]));
  if (doomed.size === 0) return project;

  const clips = { ...project.clips };
  const touchedTracks = new Set<string>();
  for (const id of doomed) {
    touchedTracks.add(clips[id].trackId);
    delete clips[id];
  }

  const tracks = { ...project.tracks };
  for (const trackId of touchedTracks) {
    const track = tracks[trackId];
    if (!track) continue;
    tracks[trackId] = { ...track, clipIds: track.clipIds.filter((id) => !doomed.has(id)) };
  }
  return { ...project, clips, tracks };
}

/** Re-sorts one track's clip list after clips have moved. */
export function resortTrackClips(project: Project, trackId: string): Project {
  const track = project.tracks[trackId];
  if (!track) return project;
  const sorted = [...track.clipIds].sort(
    (a, b) => (project.clips[a]?.startTicks ?? 0) - (project.clips[b]?.startTicks ?? 0),
  );
  const unchanged = sorted.length === track.clipIds.length && sorted.every((id, i) => id === track.clipIds[i]);
  if (unchanged) return project;
  return { ...project, tracks: { ...project.tracks, [trackId]: { ...track, clipIds: sorted } } };
}

/** Moves a clip between tracks, maintaining both tracks' clip lists. */
export function reparentClip(project: Project, clipId: string, newTrackId: string): Project {
  const clip = project.clips[clipId];
  const target = project.tracks[newTrackId];
  if (!clip || !target || clip.trackId === newTrackId) return project;

  const oldTrack = project.tracks[clip.trackId];
  const tracks = { ...project.tracks };
  if (oldTrack) tracks[oldTrack.id] = { ...oldTrack, clipIds: oldTrack.clipIds.filter((id) => id !== clipId) };
  const updatedClip = { ...clip, trackId: newTrackId };
  const clips = { ...project.clips, [clipId]: updatedClip };
  tracks[newTrackId] = {
    ...target,
    clipIds: [...target.clipIds.filter((id) => id !== clipId), clipId].sort(
      (a, b) => (clips[a]?.startTicks ?? 0) - (clips[b]?.startTicks ?? 0),
    ),
  };
  return { ...project, clips, tracks };
}

export function touchProject(project: Project): Project {
  return { ...project, meta: { ...project.meta, modifiedAt: Date.now() } };
}
